"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import Swal from "sweetalert2"
import { adminLogin, setAuthData, isAuthenticated } from "../api/auth"

export default function AdminLogin() {
  const navigate = useNavigate()
  const [form, setForm] = useState({ email: "", password: "" })
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  useEffect(() => {
    if (isAuthenticated()) {
      navigate("/admin", { replace: true })
    }
  }, [navigate])

  const handleChange = (e) => {
    setForm((s) => ({ ...s, [e.target.name]: e.target.value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!form.email || !form.password) {
      Swal.fire({
        icon: "warning",
        title: "Missing Fields",
        text: "Please enter both email and password",
        confirmButtonColor: "#004aad",
      })
      return
    }

    setLoading(true)

    try {
      const response = await adminLogin(form.email, form.password)

      if (response.success) {
        // Store auth data
        setAuthData(response.data.admin, response.data.token)

        // Show success message
        await Swal.fire({
          icon: "success",
          title: "Login Successful",
          text: `Welcome back, ${response.data.admin.name}!`,
          confirmButtonColor: "#004aad",
          timer: 1500,
          showConfirmButton: false,
        })

        // Navigate to admin dashboard
        navigate("/admin", { replace: true })
      } else {
        throw new Error(response.message || "Login failed")
      }
    } catch (error) {
      // Show error message
      Swal.fire({
        icon: "error",
        title: "Login Failed",
        text: error.message || "Invalid email or password",
        confirmButtonColor: "#004aad",
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      {/* inline styles copied from the HTML page */}
      <style>{`
        .contact-header { text-align:center; padding:60px 20px 40px; }
        .contact-header h1 { font-weight:800; color:#002b5c; }
        .contact-header p { color:#6c757d; }
        .contact-card { background: var(--info-card); border-radius:16px; padding:30px;
                        box-shadow: rgba(0,0,0,0.08) 0px 6px 20px; height:100%; }
        .info-box { display:flex; align-items:start; margin-bottom:20px; }
        .info-box i { font-size:1.6rem; margin-right:15px; color:#004aad; }
        iframe { border-radius:16px; }
        .password-toggle { 
          position: absolute; 
          right: 12px; 
          top: 50%; 
          transform: translateY(-50%); 
          background: none; 
          border: none; 
          cursor: pointer; 
          color: #6c757d;
          padding: 0;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .password-toggle:hover { color: #004aad; }
        .password-input-wrapper { position: relative; }
      `}</style>

      <section className="features" style={{ height: "100vh", padding: "80px 50px", background: "#f8f9fa" }}>
        <div className="container-lg">
          <div className="row g-4 justify-content-center">
            {/* Contact Form (Login Card) */}
            <div className="col-lg-5">
              <center>
                <img src="/logo.png" alt="logo" />
              </center>
              <div className="contact-card destination mt-3 bg-white">
                <h4 className="fw-bold mb-3">Login to Super Admin</h4>

                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      name="email"
                      className="form-control"
                      placeholder="Enter Email"
                      value={form.email}
                      onChange={handleChange}
                      disabled={loading}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Password</label>
                    <div className="password-input-wrapper">
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        className="form-control"
                        placeholder="Enter Password"
                        value={form.password}
                        onChange={handleChange}
                        disabled={loading}
                        style={{ paddingRight: "40px" }}
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() => setShowPassword(!showPassword)}
                        tabIndex={-1}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                            <line x1="1" y1="1" x2="23" y2="23"></line>
                          </svg>
                        ) : (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                            <circle cx="12" cy="12" r="3"></circle>
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>

                  <button type="submit" className="btn btn-primary w-100 mt-3" disabled={loading}>
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Logging in...
                      </>
                    ) : (
                      "Login"
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
